<template>
  <Dialog
    v-model:visible="showDialog"
    header="Create Service Invoice"
    modal
    :closable="true"
    class="max-h-[90vh] overflow-auto"
  >
    <form class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <!-- General Info Section -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-3 border-r pr-5 pb-5 overflow-auto max-h-[600px]"
        :class="isDark ? 'border-gray-600' : 'border-gray-300'"
      >
        <div
          class="col-span-2 text-lg font-semibold mb-4 border-b-2 pb-2 tracking-wide"
          :class="
            isDark
              ? 'text-gray-100 border-gray-600'
              : 'text-gray-800 border-brand_orange1'
          "
        >
          General Information
        </div>

        <!-- Invoice Number -->
        <div class="w-full mb-3">
          <label class="text-sm font-medium mb-1 block">
            Invoice Number <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.invoiceNumber"
            placeholder="Invoice Number"
            class="w-full h-12 px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- Date -->
        <div class="w-full mb-3">
          <label class="text-sm font-medium mb-1 block">
            Date <span class="text-red-500">*</span>
          </label>
          <div class="w-full">
            <DatePicker
              v-model="form.date"
              placeholder="Select Date"
              :input-class="[
                'w-full h-12 px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]',
                isDark
                  ? 'bg-gray-700 text-gray-100 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300',
              ]"
              class="w-full"
            />
          </div>
        </div>

        <!-- Sold To -->
        <div class="col-span-2">
          <label class="text-sm font-medium mb-1 block"
            >Sold To <span class="text-red-500">*</span></label
          >
          <input
            v-model="form.soldTo"
            placeholder="Sold To"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- TIN -->
        <div>
          <label class="text-sm font-medium mb-1 block"
            >TIN <span class="text-red-500">*</span></label
          >
          <input
            v-model="form.tin"
            placeholder="TIN No. Or 0 if Individual"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- Terms -->
        <div>
          <label class="text-sm font-medium mb-1 block">Terms</label>
          <input
            v-model="form.terms"
            placeholder="Terms"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- Address -->
        <div class="col-span-2">
          <label class="text-sm font-medium mb-1 block"
            >Address <span class="text-red-500">*</span></label
          >
          <textarea
            v-model="form.address"
            rows="auto"
            placeholder="Address"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          ></textarea>
        </div>

        <!-- OSCA/PWD ID -->
        <div>
          <label class="text-sm font-medium mb-1 block">OSCA/PWD ID No</label>
          <input
            v-model="form.oscaId"
            placeholder="OSCA/PWD ID No"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- SC/PWD Signature -->
        <div>
          <label class="text-sm font-medium mb-1 block">SC/PWD Signature</label>
          <input
            v-model="form.signature"
            placeholder="SC/PWD Signature"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            "
          />
        </div>

        <!-- Invoice Items Section -->
        <div class="col-span-2 mt-5 max-w-[500px] mx-auto">
          <!-- Header with Add Button -->
          <div
            class="flex items-center justify-between mb-4 border-b-2 pb-2"
            :class="isDark ? 'border-gray-600' : 'border-brand_orange1'"
          >
            <span
              class="text-lg font-semibold tracking-wide"
              :class="isDark ? 'text-gray-100' : 'text-gray-800'"
            >
              Invoice Items
            </span>
            <button
              type="button"
              @click="addInvoiceItem"
              class="px-3 py-1 bg-brand_orange3 text-white text-sm rounded-md hover:bg-brand_orange1"
            >
              + Add Item
            </button>
          </div>

          <!-- Description -->
          <div class="col-span-2 mb-2">
            <label class="text-sm font-medium mb-1 block">
              Description <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="form.description"
              rows="2"
              placeholder="Description"
              class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]"
              :class="
                isDark
                  ? 'bg-gray-700 text-gray-100 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              "
            >
            </textarea>
          </div>

          <!-- Qty, Cost, Total Row -->
          <div class="grid grid-cols-3 gap-2 mb-2">
            <div>
              <label class="text-sm font-medium mb-1 block">Qty</label>
              <input
                v-model="form.quantity"
                type="number"
                placeholder="0"
                class="w-full px-2 py-2 border rounded-md text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
                :class="
                  isDark
                    ? 'bg-gray-700 text-gray-100 border-gray-600'
                    : 'bg-white text-gray-900 border-gray-300'
                "
              />
            </div>

            <div>
              <label class="text-sm font-medium mb-1 block">Cost</label>
              <input
                v-model="form.cost"
                type="number"
                placeholder="0.00"
                class="w-full px-2 py-2 border rounded-md text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
                :class="
                  isDark
                    ? 'bg-gray-700 text-gray-100 border-gray-600'
                    : 'bg-white text-gray-900 border-gray-300'
                "
              />
            </div>

            <div>
              <label class="text-sm font-medium mb-1 block">Total</label>
              <input
                v-model="form.total"
                type="number"
                placeholder="0.00"
                readonly
                class="w-full px-2 py-2 border rounded-md text-right bg-gray-100 cursor-not-allowed"
                :class="
                  isDark
                    ? 'bg-gray-700 text-gray-100 border-gray-600'
                    : 'bg-gray-100 text-gray-900 border-gray-300'
                "
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Info Section -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white dark:bg-gray-800 rounded-md shadow overflow-auto max-h-[600px]"
      >
        <div
          class="col-span-2 text-lg font-semibold mb-4 border-b-2 pb-2 tracking-wide dark:text-gray-100 text-gray-800 border-brand_orange1 dark:border-gray-600"
        >
          Payment Information
        </div>

        <!-- With Withholding Select -->
        <div>
          <label class="text-sm font-medium mb-1 block">With Withholding</label>
          <select
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>

        <!-- Mode of Payment -->
        <div>
          <label class="text-sm font-medium mb-1 block">Mode of Payment</label>
          <select
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          >
            <option value="cash">Cash</option>
            <option value="check">Check</option>
          </select>
        </div>

        <!-- Check Number -->
        <div>
          <label class="text-sm font-medium mb-1 block">Check Number</label>
          <input
            type="text"
            placeholder="Check Number"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          />
        </div>

        <!-- Bank -->
        <div>
          <label class="text-sm font-medium mb-1 block">Bank</label>
          <input
            type="text"
            placeholder="Bank"
            class="px-4 py-3 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          />
        </div>

        <!-- VATable Sales -->
        <div>
          <label class="text-sm font-medium mb-1 block"
            >VATable Sales <span class="text-red-500"> *</span></label
          >
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          />
        </div>

        <!-- VAT Exempt Sales -->
        <div>
          <label class="text-sm font-medium mb-1 block"
            >VAT Exempt Sales <span class="text-red-500"> *</span></label
          >
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          />
        </div>

        <!-- Zero-Rated Sales -->
        <div>
          <label class="text-sm font-medium mb-1 block"
            >Zero-Rated Sales <span class="text-red-500"> *</span></label
          >
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
          />
        </div>

        <!-- VAT Amount -->
        <div>
          <label class="text-sm font-medium mb-1 block">VAT Amount</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Amount -->
        <div>
          <label class="text-sm font-medium mb-1 block">Amount</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Less VAT -->
        <div>
          <label class="text-sm font-medium mb-1 block">Less VAT</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Amount Net of VAT -->
        <div>
          <label class="text-sm font-medium mb-1 block">Amount Net of VAT</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Less SC/PWD Discount -->
        <div>
          <label class="text-sm font-medium mb-1 block">Less SC/PWD Discount</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Less Withholding Tax -->
        <div>
          <label class="text-sm font-medium mb-1 block">Less Withholding Tax</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>

        <!-- Total Amount Due -->
        <div>
          <label class="text-sm font-medium mb-1 block">Total Amount Due</label>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            class="px-4 py-3 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]"
            disabled
          />
        </div>
      </div>
    </form>

    <!-- Buttons -->
    <div class="mt-4 flex justify-end gap-2">
      <!-- Save & Close (Primary) -->
      <button
        @click.prevent
        class="px-4 py-2 rounded-md bg-brand_orange3 text-white hover:bg-[#e07b1c] shadow-md"
      >
        Save & Close
      </button>

      <!-- Save & Create New (Secondary) -->
      <button
        @click.prevent
        class="px-4 py-2 rounded-md bg-orange-200 text-orange-900 hover:bg-orange-300"
      >
        Save & Create New
      </button>
    </div>
  </Dialog>

  <!-- Sidebar Section -->

  <div
    class="flex h-screen"
    :class="isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-900'"
  >
    <!-- Sidebar -->
    <div
      class="w-16 lg:w-64 p-4 hidden md:flex flex-col justify-between shadow-lg transition-all duration-300"
      :class="isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'"
    >
      <!-- Sidebar top -->
      <div>
        <!-- IWC Logo -->
        <div class="flex gap-2 items-center mb-6">
          <img
            class="w-8 h-8 md:w-10 md:h-7 lg:w-12 lg:h-12"
            src="../assets/logo.png"
            alt="Logo"
          />
          <h2 class="text-m font-bold hidden lg:block">Service Invoice Portal</h2>
        </div>
        <div class="space-y-2 mt-8">
          <!-- Service Invoice -->
          <div
            class="p-2 rounded cursor-pointer flex items-center gap-2 transition-colors"
            :class="
              isDark
                ? 'bg-gray-700 hover:bg-[#FDCE96] hover:text-gray-900'
                : 'bg-gray-200 hover:bg-[#FDCE96] hover:text-gray-900'
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h18v18H3V3z" />
            </svg>
            <span class="hidden lg:inline">Service Invoice</span>
          </div>

          <!-- System Users -->
          <div
            class="p-2 rounded cursor-pointer flex items-center gap-2 transition-colors"
            :class="
              isDark
                ? 'bg-gray-700 hover:bg-[#FDCE96] hover:text-gray-900'
                : 'bg-gray-200 hover:bg-[#FDCE96] hover:text-gray-900'
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5 lg:w-8"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            <span class="hidden lg:inline">System Users</span>
          </div>
        </div>
      </div>

      <!-- Sidebar footer -->
      <div class="space-y-2">
        <!-- Settings -->
        <div
          class="p-2 rounded flex items-center gap-2 cursor-pointer transition-colors"
          :class="
            isDark
              ? 'bg-gray-700 hover:bg-[#FDCE96] hover:text-gray-900'
              : 'bg-gray-200 hover:bg-[#FDCE96] hover:text-gray-900'
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 6h3m-7.5 6h12m-12 6h7.5"
            />
          </svg>
          <span class="hidden lg:inline">Settings</span>
        </div>

        <!-- User info -->
        <div
          class="p-2 rounded flex items-center gap-2"
          :class="isDark ? 'bg-gray-700' : 'bg-gray-200'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 7.5a3.75 3.75 0 11-7.5 0 
                 3.75 3.75 0 017.5 0zM4.5 
                 20.25a8.25 8.25 0 1115 
                 0v.75H4.5v-.75z"
            />
          </svg>
          <span class="hidden lg:inline">Sample User ( Admin )</span>
        </div>

        <!-- Logout -->
        <div
          class="p-2 rounded flex items-center gap-2 cursor-pointer text-red-400 hover:text-red-600 transition-colors"
          :class="
            isDark ? 'bg-gray-700 hover:bg-[#FDCE96]' : 'bg-gray-200 hover:bg-[#FDCE96]'
          "
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 9V5.25A2.25 2.25 0 
                 0013.5 3h-6A2.25 2.25 0 
                 005.25 5.25v13.5A2.25 2.25 
                 0 007.5 21h6a2.25 2.25 0 
                 002.25-2.25V15m3 0l3-3m0 
                 0l-3-3m3 3H9"
            />
          </svg>
          <span class="hidden lg:inline">Logout</span>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex-1 p-6 flex flex-col">
      <!-- Top bar -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <!-- Burger button -->
          <button
            @click="sidebarOpen = !sidebarOpen"
            class="md:hidden p-2 rounded hover:bg-[#FDCE96] hover:text-gray-900"
            :class="isDark ? 'text-gray-100' : 'text-gray-900'"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 5.25h16.5M3.75 12h16.5M3.75 18.75h16.5"
              />
            </svg>
          </button>

          <h1 class="text-2xl font-bold">Main Content</h1>
        </div>

        <!-- Dark toggle -->
        <button
          @click="toggleDark"
          class="px-3 py-1 rounded hover:opacity-80"
          :class="isDark ? 'bg-gray-600 text-gray-100' : 'bg-gray-300 text-gray-900'"
        >
          {{ isDark ? "Dark" : "Light" }}
        </button>
      </div>

      <!-- Main Content Line Division -->
      <hr class="border-t border-gray-300 dark:border-gray-600 mb-4" />

      <!-- Main body / table or content -->
      <div class="flex-1">
        <!-- Table header + search + create button -->
        <div
          class="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3 md:gap-0"
        >
          <h2 class="text-lg font-semibold">Invoice List</h2>

          <div class="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
            <!-- Create Invoice Button -->
            <button
              @click="showDialog = true"
              class="px-4 py-2 flex items-center gap-2 bg-brand_orange3 text-white font-semibold rounded-md hover:bg-[#e07b1c] dark:bg-[#d97706] dark:hover:bg-[#b95b05] w-full md:w-auto justify-center transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Create Invoice
            </button>

            <!-- Search bar -->
            <div class="relative w-full md:w-64 flex-shrink-0">
              <input
                type="text"
                placeholder="Search..."
                class="pl-10 pr-4 py-2 border rounded-md text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"
                :class="
                  isDark
                    ? 'bg-gray-700 text-gray-100 border-gray-600 placeholder-gray-400'
                    : 'bg-white text-gray-900 border-gray-300 placeholder-gray-500'
                "
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5 absolute left-3 top-2.5"
                :class="isDark ? 'text-gray-300' : 'text-gray-400'"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Invoice Table -->
        <div
          class="overflow-auto rounded-md border"
          :class="isDark ? 'border-gray-600' : 'border-gray-300'"
        >
          <table class="min-w-full table-auto border-collapse">
            <!-- Table Head -->
            <thead :class="isDark ? 'bg-gray-800' : 'bg-gray-50'">
              <tr>
                <th class="px-4 py-4 text-left text-base font-semibold w-36">
                  Invoice No.
                </th>
                <th class="px-4 py-4 text-left text-base font-semibold min-w-[150px]">
                  Sold To
                </th>
                <th class="px-4 py-4 text-left text-base font-semibold min-w-[150px]">
                  Address
                </th>
                <th class="px-4 py-4 text-left text-base font-semibold">Description</th>
                <th class="px-4 py-4 text-left text-base font-semibold w-28">Total</th>
                <th class="px-4 py-4 text-left text-base font-semibold w-32">Date</th>
                <th class="px-4 py-4 text-left text-base font-semibold w-20"></th>
                <th class="px-4 py-4 text-left text-base font-semibold w-20"></th>
              </tr>
            </thead>

            <!-- Table Body -->
            <tbody>
              <!-- Row example -->
              <tr
                :class="[
                  isDark
                    ? 'bg-gray-900 hover:bg-gray-700'
                    : 'bg-white hover:bg-[var(--brand_orange1)]',
                  'transition-colors duration-150 border-b border-gray-200',
                ]"
              >
                <td class="px-4 py-4 text-sm w-36">INV-2025-001</td>
                <td class="px-4 py-4 text-sm min-w-[150px]">Juan Dela Cruz</td>
                <td class="px-4 py-4 text-sm min-w-[150px]">123 Mabini St, Manila</td>
                <td class="px-4 py-4 text-sm">Consultation Fee for General Checkup</td>
                <td class="px-4 py-4 text-sm w-28">1,200.00</td>
                <td class="px-4 py-4 text-sm w-32">2025-10-06</td>
                <td class="px-4 py-4 text-sm w-20">
                  <button
                    class="w-full py-1 px-2 bg-brand_orange3 text-white font-semibold rounded hover:bg-[#e07b1c] transition"
                  >
                    Edit
                  </button>
                </td>
                <td class="px-4 py-4 text-sm w-20">
                  <button
                    class="w-full py-1 px-2 bg-brand_orange3 text-white font-semibold rounded hover:bg-[#e07b1c] transition"
                  >
                    Preview
                  </button>
                </td>
              </tr>

              <tr
                :class="[
                  isDark
                    ? 'bg-gray-900 hover:bg-gray-700'
                    : 'bg-white hover:bg-[var(--brand_orange1)]',
                  'transition-colors duration-150 border-b border-gray-200',
                ]"
              >
                <td class="px-4 py-4 text-sm w-36">INV-2025-002</td>
                <td class="px-4 py-4 text-sm min-w-[150px]">Maria Santos</td>
                <td class="px-4 py-4 text-sm min-w-[150px]">
                  456 Rizal Ave, Quezon City
                </td>
                <td class="px-4 py-4 text-sm">Laboratory Fees – Blood Work</td>
                <td class="px-4 py-4 text-sm w-28">850.00</td>
                <td class="px-4 py-4 text-sm w-32">2025-10-06</td>
                <td class="px-4 py-4 text-sm w-20">
                  <button
                    class="w-full py-1 px-2 bg-brand_orange3 text-white font-semibold rounded hover:bg-[#e07b1c] transition"
                  >
                    Edit
                  </button>
                </td>
                <td class="px-4 py-4 text-sm w-20">
                  <button
                    class="w-full py-1 px-2 bg-brand_orange3 text-white font-semibold rounded hover:bg-[#e07b1c] transition"
                  >
                    Preview
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Paginator -->
      <div class="center-all items-center my-4 space-x-2">
        <button
          class="px-3 py-1 rounded-md border"
          :class="
            isDark
              ? 'bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700'
              : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-100'
          "
        >
          Previous
        </button>

        <!-- Page numbers -->
        <div class="flex items-center space-x-1">
          <button
            class="px-3 py-1 rounded-md"
            :class="
              isDark
                ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
            "
          >
            1
          </button>
          <button
            class="px-3 py-1 rounded-md"
            :class="
              isDark
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-white text-gray-900 hover:bg-gray-100'
            "
          >
            2
          </button>
          <button
            class="px-3 py-1 rounded-md"
            :class="
              isDark
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-white text-gray-900 hover:bg-gray-100'
            "
          >
            3
          </button>
        </div>

        <button
          class="px-3 py-1 rounded-md border"
          :class="
            isDark
              ? 'bg-gray-800 text-gray-100 border-gray-600 hover:bg-gray-700'
              : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-100'
          "
        >
          Next
        </button>
      </div>

      <!-- Footer -->
      <div class="text-center text-sm mt-6 text-gray-600 dark:text-gray-400">
        Version 1.0.0
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";

export default {
  setup() {
    const showDialog = ref(false);
    const isDark = ref(false);
    const toggleDark = () => {
      isDark.value = !isDark.value;
    };

    const form = ref({
      invoiceNumber: "",
      date: "",
      soldTo: "",
      terms: "",
      tin: "",
      oscaId: "",
      address: "",
      signature: "",
    });

    const submitInvoice = () => {
      console.log("Form data:", form.value);
      showDialog.value = false;
    };

    return { isDark, showDialog, form, toggleDark, submitInvoice };
  },
};
</script>
